import React, { useState, useEffect } from 'react';
import { api } from '@/api/apiClient';
import { Link } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Heart,
  DollarSign,
  Calendar,
  FileText,
  CheckCircle2,
  Clock,
  XCircle,
  ArrowRight,
  Download,
  Shield
} from 'lucide-react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { calculateEmployeeTaxes } from '@/components/utils/dynamicTaxCalculations';
import { generateBenefitSummaryPDF, downloadPDF } from '@/components/utils/pdfGeneration';
import { toast } from 'sonner';

export default function MyBenefits() {
  const [enrollments, setEnrollments] = useState([]);
  const [products, setProducts] = useState([]);
  const [employee, setEmployee] = useState(null);
  const [totalTaxSavings, setTotalTaxSavings] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const user = await api.auth.me();
      const employees = await api.entities.Employee.filter({ user_email: user.email });
      
      if (employees.length > 0) {
        const emp = employees[0];
        setEmployee(emp);
        
        const [enrollmentsData, productsData] = await Promise.all([
          api.entities.Enrollment.filter({ employee_id: emp.id }),
          api.entities.BenefitProduct.list()
        ]);
        
        setEnrollments(enrollmentsData);
        setProducts(productsData);
        
        // Calculate total tax savings
        const activePremiums = enrollmentsData
          .filter(e => e.status === 'active')
          .reduce((sum, e) => sum + (Number.parseFloat(e.monthly_premium) || Number.parseFloat(e.employee_cost) || 0), 0);
        
        if (activePremiums > 0) {
          try {
            const taxAnalysis = await calculateEmployeeTaxes(emp, activePremiums * 12);
            setTotalTaxSavings(taxAnalysis.savings.total);
          } catch {}
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getProduct = (productId) => products.find(p => p.id === productId);

  const handleDownloadSummary = async () => {
    if (!employee || activeEnrollments.length === 0) {
      toast.error('No benefits to download');
      return;
    }

    try {
      toast.info('Generating PDF...');
      const employers = await api.entities.Employer.filter({ id: employee?.employer_id });
      const pdf = await generateBenefitSummaryPDF(employee, activeEnrollments, products, employers[0]);
      downloadPDF(pdf, `benefits_summary_${employee.first_name}_${employee.last_name}.pdf`);
      toast.success('Benefits summary downloaded successfully!');
    } catch (err) {
      console.error('PDF generation error:', err);
      toast.error('Failed to generate PDF. Please try again.');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: { bg: 'bg-emerald-100', text: 'text-emerald-700', icon: CheckCircle2 },
      pending: { bg: 'bg-amber-100', text: 'text-amber-700', icon: Clock },
      cancelled: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle },
      expired: { bg: 'bg-slate-100', text: 'text-slate-700', icon: XCircle },
    };
    const style = styles[status] || styles.pending;
    const Icon = style.icon;
    
    return (
      <Badge className={`${style.bg} ${style.text}`}>
        <Icon className="w-3 h-3 mr-1" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const activeEnrollments = enrollments.filter(e => e.status === 'active');
  const pendingEnrollments = enrollments.filter(e => e.status === 'pending');
  const pastEnrollments = enrollments.filter(e => ['cancelled', 'expired'].includes(e.status));

  const totalMonthlyPremium = activeEnrollments.reduce((sum, e) => sum + (Number.parseFloat(e.monthly_premium) || Number.parseFloat(e.employee_cost) || 0), 0);
  const totalCoverage = activeEnrollments.reduce((sum, e) => sum + (Number.parseFloat(e.coverage_amount) || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">My Benefits</h1>
          <p className="text-slate-500 mt-1">
            View and manage your enrolled benefits
          </p>
        </div>
        <div className="flex gap-3">
          <Button 
            variant="outline"
            onClick={handleDownloadSummary}
            disabled={!employee || activeEnrollments.length === 0}
          >
            <Download className="w-4 h-4 mr-2" />
            Download Summary
          </Button>
          <Link to={createPageUrl('BenefitsCatalog')}>
            <Button className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600">
              <Heart className="w-4 h-4 mr-2" />
              Browse More Benefits
            </Button>
          </Link>
        </div>
      </div>

      {/* Summary Cards */}
      <div className={`grid gap-6 ${totalTaxSavings > 0 ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                  <Heart className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-emerald-100 text-sm">Active Benefits</p>
                  <p className="text-3xl font-bold">{activeEnrollments.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <Card className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                  <DollarSign className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-blue-100 text-sm">Monthly Premium</p>
                  <p className="text-3xl font-bold">${totalMonthlyPremium.toFixed(2)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <Card className="bg-gradient-to-br from-purple-500 to-pink-600 text-white">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                  <Shield className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-purple-100 text-sm">Total Coverage</p>
                  <p className="text-3xl font-bold">${totalCoverage.toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {totalTaxSavings > 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <Card className="bg-gradient-to-br from-amber-500 to-orange-600 text-white">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                    <DollarSign className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-amber-100 text-sm">Annual Tax Savings</p>
                    <p className="text-3xl font-bold">${totalTaxSavings.toFixed(0)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </div>

      {/* Benefits Tabs */}
      <Tabs defaultValue="active" className="w-full">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="active" className="data-[state=active]:bg-white">
            Active ({activeEnrollments.length})
          </TabsTrigger>
          <TabsTrigger value="pending" className="data-[state=active]:bg-white">
            Pending ({pendingEnrollments.length})
          </TabsTrigger>
          <TabsTrigger value="history" className="data-[state=active]:bg-white">
            History ({pastEnrollments.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="active" className="mt-6">
          {activeEnrollments.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Heart className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-700 mb-2">No Active Benefits</h3>
                <p className="text-slate-500 mb-4">You haven't enrolled in any benefits yet.</p>
                <Link to={createPageUrl('BenefitsCatalog')}>
                  <Button className="bg-emerald-500 hover:bg-emerald-600">
                    Browse Benefits
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {activeEnrollments.map((enrollment, idx) => {
                const product = getProduct(enrollment.product_id);
                return (
                  <motion.div
                    key={enrollment.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.1 }}
                  >
                    <Card className="hover:shadow-lg transition-shadow">
                      <CardContent className="p-6">
                        <div className="flex flex-col md:flex-row md:items-center gap-4">
                          <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center flex-shrink-0">
                            <Heart className="w-8 h-8 text-white" />
                          </div>
                          
                          <div className="flex-1 space-y-2">
                            <div className="flex items-center gap-3">
                              <h3 className="text-lg font-semibold text-slate-900">
                                {product?.name || 'Unknown Benefit'}
                              </h3>
                              {getStatusBadge(enrollment.status)}
                            </div>
                            
                            <p className="text-slate-600 text-sm">
                              {product?.short_description}
                            </p>
                            
                            <div className="flex flex-wrap gap-4 text-sm">
                              <div className="flex items-center gap-1 text-slate-600">
                                <DollarSign className="w-4 h-4" />
                                <span className="font-medium">${(Number.parseFloat(enrollment.monthly_premium) || Number.parseFloat(enrollment.employee_cost) || 0).toFixed(2)}</span>
                                <span>/month</span>
                              </div>
                              
                              {enrollment.coverage_amount && (
                                <div className="flex items-center gap-1 text-slate-600">
                                  <Shield className="w-4 h-4" />
                                  <span className="font-medium">${enrollment.coverage_amount.toLocaleString()}</span>
                                  <span>coverage</span>
                                </div>
                              )}
                              
                              {enrollment.effective_date && (
                                <div className="flex items-center gap-1 text-slate-600">
                                  <Calendar className="w-4 h-4" />
                                  <span>Effective: {format(new Date(enrollment.effective_date), 'MMM d, yyyy')}</span>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex gap-2">
                            <Link to={createPageUrl(`BenefitDetail?id=${enrollment.product_id}`)}>
                              <Button variant="outline" size="sm">
                                View Details
                              </Button>
                            </Link>
                            {enrollment.policy_document_url && (
                              <Button variant="outline" size="sm">
                                <Download className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="pending" className="mt-6">
          {pendingEnrollments.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Clock className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-700">No Pending Enrollments</h3>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {pendingEnrollments.map((enrollment) => {
                const product = getProduct(enrollment.product_id);
                return (
                  <Card key={enrollment.id}>
                    <CardContent className="p-6 flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                        <Clock className="w-6 h-6 text-amber-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-slate-900">{product?.name}</h3>
                        <p className="text-sm text-slate-500">Enrollment pending approval</p>
                      </div>
                      {getStatusBadge(enrollment.status)}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="history" className="mt-6">
          {pastEnrollments.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-700">No Past Enrollments</h3>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {pastEnrollments.map((enrollment) => {
                const product = getProduct(enrollment.product_id);
                return (
                  <Card key={enrollment.id} className="bg-slate-50">
                    <CardContent className="p-6 flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-slate-200 flex items-center justify-center">
                        <Heart className="w-6 h-6 text-slate-400" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-slate-700">{product?.name}</h3>
                        <p className="text-sm text-slate-500">
                          {enrollment.end_date && `Ended: ${format(new Date(enrollment.end_date), 'MMM d, yyyy')}`}
                        </p>
                      </div>
                      {getStatusBadge(enrollment.status)}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}